import ChartColumn20 from '@carbon/icons-react/es/chart--column/20';
import { useBoolean } from 'ahooks';
import { keyBy } from 'lodash';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import {
  AggregateType,
  ChartFilter,
  FieldClass,
  NumberWidgetTypes,
  UnitPosition,
  WidgetNativeValues,
  WidgetType,
  ZoomLevel,
} from 'api/dashboard/widget';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { PageLoading } from 'components/atoms/loading';
import { toastService } from 'components/molecules/toast';
import {
  CustomCardFormValues,
  CustomCards,
} from 'components/organisms/dashboard/components/custom-cards';
import {
  CustomChart,
  CustomChartFormValues,
} from 'components/organisms/dashboard/components/custom-chart';
import { ChartType } from 'components/organisms/dashboard/components/types';
import {
  byNumberTypeWidget,
  setAggType,
  transformField,
  useGetChartAndCardWidgetCount,
  useProjectFieldOptions,
} from 'components/organisms/dashboard/components/utils';
import {
  scrollToBottomOfDashboard,
  scrollToTopOfDashboard,
} from 'components/organisms/dashboard/scroll';
import { useDashboardInfo } from 'components/organisms/dashboard/useDashboardInfo';
import { ButtonGroup } from 'components/organisms/task-pane/task-detail/header/add-time-entry';
import { QueryCombinerType } from 'components/organisms/tasks/combiner';
import { excludedAllTasksFields } from 'components/organisms/tasks/constants';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import {
  useFieldsAsDefaultGroupedFields,
  useFieldsAsGroupedFields,
} from 'components/pages/all-tasks/use-fields-as-grouped-fields';
import { getApiGroupedFilterTransformer } from 'components/pages/api-grouped-filter-transformer';
import { GroupFieldsMeta } from 'components/pages/filter-transformer';
import { FilterGroupType } from 'components/pages/types';
import { RootState } from 'data';
import { useTranslate } from 'hooks/use-translation';
import { useQueryJiraConfig } from 'queries/jira-integration';
import { useCreateWidgetMutation } from 'queries/widgets';
import { FieldsStore, TaskField } from 'stores/fields';
import { excludedProjectFieldsForFilter } from 'utils/project-fields/constants';
import { useProjectFieldsAsFilterableFields } from 'utils/project-fields/use-project-fields-as-filterable-fields';
import { unreachable } from 'utils/unreachable';
import { useGetDashboardUrlWithSearch } from 'utils/urls';
import { DashBoardDrawer, PrimaryTitle, SecondaryTitle } from './drawer';
import { StepOne, useWidgetTypeToName } from './step-one';
import { FormValues, StepTwo } from './step-two';

enum Step {
  One = 1,
  Two,
  Three,
  Four,
}

const customChartInitialValues = {
  widgetName: 'Untitled',
  unitName: '',
  objectType: 'PROJECT',
  zoomLevel: ZoomLevel.Monthly,
  aggType: AggregateType.Total,
  chartType: {
    label: 'Column',
    value: ChartType.Column,
    icon: <ChartColumn20 />,
  },
  xAxis: {
    label: 'Project Start Date',
    value: {
      name: 'startDate',
      fieldClass: FieldClass.NativeField,
    },
  },
  yAxis: {
    label: 'Project Count',
    value: {
      name: 'count',
      fieldClass: FieldClass.NativeField,
    },
  },
};

export const AddWidgetPage: FC = () => {
  const translate = useTranslate();
  const widgetTypeToName = useWidgetTypeToName();
  const { dashboardId } = useParams<{ dashboardId: string }>();

  const [visible, { setFalse: closeDrawer }] = useBoolean(true);
  const [currentStep, setCurrentStep] = useState<Step>(Step.One);
  const { fields: allFields } = useProjectFieldsAsFilterableFields();
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const { data: jiraIntegrationConfig } = useQueryJiraConfig();
  const [form] = Form.useForm<FormValues>();
  const [cardFieldsForm] = Form.useForm();
  const [chartFieldsForm] = Form.useForm();
  const getDashboardUrl = useGetDashboardUrlWithSearch();

  const { chartTypeWidgetsCount, numberTypeWidgetsCount } =
    useGetChartAndCardWidgetCount(Number(dashboardId));

  const excludedFields = useMemo(() => {
    // remove jiraIntegration from excluded fields ONLY when the integration is active
    if (jiraIntegrationConfig?.length) {
      return excludedAllTasksFields.filter(
        (field) => field !== 'jiraIntegration',
      );
    }

    return excludedAllTasksFields;
  }, [jiraIntegrationConfig]);
  const taskFields: TaskField[] = useMemo(
    () =>
      FieldsStore.enabledTaskFields(fieldsStore).filter(
        ({ fieldName }) => !excludedFields.includes(fieldName),
      ),
    [fieldsStore, excludedFields],
  );

  const projectFields = allFields.filter(
    (x) => !excludedProjectFieldsForFilter.includes(x.fieldName),
  );

  const { groupedProjectNumberFieldOptions, isfieldsLoading } =
    useProjectFieldOptions(WidgetType.CustomCard);

  const [filter, setFilter] = useState<GroupedFilter>({
    type: QueryCombinerType.All,
    groupCriteria: [],
    defaultGroup: FilterGroupType.Project,
  });

  const [customCardsFilter, setCustomCardsFilter] = useState<GroupedFilter>({
    type: QueryCombinerType.All,
    groupCriteria: [],
    defaultGroup: FilterGroupType.Project,
  });

  const [customChartsFilter, setCustomChartsFilter] = useState<ChartFilter>({
    projectsFilter: {
      type: QueryCombinerType.All,
      groupCriteria: [],
      defaultGroup: FilterGroupType.Project,
    },
    tasksFilter: {
      type: QueryCombinerType.All,
      groupCriteria: [],
      defaultGroup: FilterGroupType.Task,
    },
  });

  const projectFieldsLookupByName = useMemo(
    () => keyBy(projectFields, 'fieldName'),
    [projectFields],
  );
  const taskFieldsLookupByName = useMemo(
    () => keyBy(taskFields, 'fieldName'),
    [taskFields],
  );
  const groups: GroupFieldsMeta = useMemo(
    () => [
      {
        id: FilterGroupType.Project,
        fields: projectFieldsLookupByName,
      },
      {
        id: FilterGroupType.Task,
        fields: taskFieldsLookupByName,
      },
    ],
    [projectFieldsLookupByName, taskFieldsLookupByName],
  );
  const { groupedFields } = useFieldsAsGroupedFields({
    taskFields,
    projectFields,
  });
  const { defaultGroupedFields } =
    useFieldsAsDefaultGroupedFields(projectFields);

  const transformer = useMemo(
    () => getApiGroupedFilterTransformer(groups, FilterGroupType.Project),
    [groups],
  );

  const history = useHistory();
  const {
    selectedDashboard: { selectedId },
  } = useDashboardInfo();
  const handleVisibleChange = (visibility: boolean) => {
    if (!visibility) {
      history.push(getDashboardUrl(selectedId));
    }
  };

  const [selectedWidgetType, setSelectedWidgetType] = useState(
    WidgetType.CustomCard,
  );

  const { mutate, isLoading } = useCreateWidgetMutation();

  const handleSubmit = () => {
    switch (currentStep) {
      case Step.Three:
        cardFieldsForm.submit();
        break;
      case Step.Four:
        chartFieldsForm.submit();
        break;
      default:
        form.submit();
        break;
    }
  };

  const onCreateWidgetSuccess = useCallback(
    async (res) => {
      const card = translate('dashboard.card');
      const chart = translate('dashbaord.chart');
      const message = translate('dashboard.widget.create.success.message', {
        widgetName: res.widgetName,
        widgetType: [
          WidgetType.CustomCard,
          WidgetType.ProjectCount,
          WidgetType.ARR,
        ].includes(selectedWidgetType)
          ? card
          : chart,
      });
      const title = translate('success');

      toastService({
        title,
        message,
      }).success();
      closeDrawer();
      if (NumberWidgetTypes.includes(res.widgetType)) {
        window.setTimeout(scrollToTopOfDashboard, 400);
      } else {
        window.setTimeout(scrollToBottomOfDashboard, 400);
      }
    },
    [closeDrawer, selectedWidgetType, translate],
  );

  const handleWidgetType = (latest: WidgetType) => {
    setSelectedWidgetType(latest);
    const values: FormValues = {
      widgetName: widgetTypeToName(latest),
    };

    form.setFieldsValue(values);
  };

  const createWidget = (values: FormValues) => {
    mutate(
      {
        dashboardId: Number(dashboardId),
        simpleSearchFilter: transformer.to(filter),
        widgetType: selectedWidgetType,
        widgetOrder: byNumberTypeWidget(selectedWidgetType)
          ? numberTypeWidgetsCount
          : chartTypeWidgetsCount,
        ...values,
      },
      {
        onSuccess: onCreateWidgetSuccess,
      },
    );
  };

  const createCustomCardWidget = (values: CustomCardFormValues) => {
    const { widgetName, prefix, field, unitName, aggType } = values;

    mutate(
      {
        dashboardId: Number(dashboardId),
        simpleSearchFilter: transformer.to(customCardsFilter),
        widgetName,
        widgetOrder: numberTypeWidgetsCount,
        widgetType: selectedWidgetType,
        metaData: {
          aggType,
          unitName: unitName || undefined,
          field: field.value,
          unitType: prefix ? UnitPosition.Prefix : UnitPosition.Suffix,
        },
      },
      {
        onSuccess: onCreateWidgetSuccess,
      },
    );
  };

  const createCustomChartWidget = useCallback(
    (values: CustomChartFormValues) => {
      const { widgetName, objectType, aggType, chartType } = values;
      const filterOptions =
        objectType === 'PROJECT'
          ? customChartsFilter.projectsFilter
          : customChartsFilter.tasksFilter;

      mutate(
        {
          dashboardId: Number(dashboardId),
          simpleSearchFilter: transformer.to(filterOptions),
          widgetName,
          widgetOrder: chartTypeWidgetsCount,
          widgetObjectType: objectType,
          widgetType: WidgetType.CustomChart,
          metaData: {
            aggType: setAggType(values.yAxis, aggType),
            chart: {
              chartType: chartType.value.toUpperCase(),
            },
            xAxis: transformField(values.xAxis),
            yAxis: transformField(values.yAxis),
            ...(values?.groupBy && { groupBy: values?.groupBy?.value }),
            ...(values.zoomLevel && { zoomLevel: values.zoomLevel }),
          },
        },
        {
          onSuccess: onCreateWidgetSuccess,
        },
      );
    },

    [
      chartTypeWidgetsCount,
      customChartsFilter.projectsFilter,
      customChartsFilter.tasksFilter,
      dashboardId,
      mutate,
      onCreateWidgetSuccess,
      transformer,
    ],
  );

  const switchStep = () => {
    switch (selectedWidgetType) {
      case WidgetType.CustomCard:
        setCurrentStep(Step.Three);
        break;
      case WidgetType.CustomChart:
        setCurrentStep(Step.Four);
        break;
      default:
        setCurrentStep(Step.Two);
    }
  };

  const getFooter = (step: Step) => {
    switch (step) {
      case Step.One:
        return (
          <ButtonGroup $reversed>
            <Button
              disabled={!selectedWidgetType}
              loading={isLoading}
              iconPlacement="right"
              type="primary"
              onClick={switchStep}
              data-cy="dashboard.add-widget.next"
            >
              <FormattedMessage id="next" />
            </Button>
            <Button onClick={closeDrawer}>
              <FormattedMessage id="cancel" />
            </Button>
          </ButtonGroup>
        );
      case Step.Two:
        return (
          <ButtonGroup $reversed>
            <Button
              loading={isLoading}
              iconPlacement="right"
              type="primary"
              onClick={handleSubmit}
              data-cy="dashboard.add-widget.save"
            >
              <FormattedMessage id="add" />
            </Button>
            <Button onClick={() => setCurrentStep(Step.One)}>
              <FormattedMessage id="back" />
            </Button>
          </ButtonGroup>
        );
      case Step.Three:
      case Step.Four:
        return (
          <ButtonGroup $reversed>
            <Button
              loading={isLoading}
              iconPlacement="right"
              type="primary"
              onClick={handleSubmit}
              data-cy="dashboard.add-widget.save"
            >
              <FormattedMessage id="create" />
            </Button>
            <Button onClick={() => setCurrentStep(Step.One)}>
              <FormattedMessage id="back" />
            </Button>
          </ButtonGroup>
        );
      default:
        return unreachable(step);
    }
  };

  const content = () => {
    switch (currentStep) {
      case Step.One:
        return (
          <StepOne
            selectedWidgetType={selectedWidgetType}
            setSelectedWidgetType={handleWidgetType}
          />
        );
      case Step.Two:
        return (
          <StepTwo
            form={form}
            fields={
              selectedWidgetType === WidgetType.TaskByStatus
                ? groupedFields
                : defaultGroupedFields
            }
            onFinish={createWidget}
            filter={filter}
            setFilter={setFilter}
            initialValues={{
              widgetName: widgetTypeToName(selectedWidgetType),
            }}
          />
        );
      case Step.Three:
        return isfieldsLoading ? (
          <PageLoading />
        ) : (
          <CustomCards
            onFinish={createCustomCardWidget}
            groupedFields={defaultGroupedFields}
            defaultGroup={FilterGroupType.Project}
            transformer={transformer}
            form={cardFieldsForm}
            filterFields={customCardsFilter}
            numberFieldOptions={groupedProjectNumberFieldOptions}
            setFilter={setCustomCardsFilter}
            initialValues={{
              widgetName: translate('dashboard.custom_card.card_name.default'),
              unitName: '',
              aggType: AggregateType.Total,
              field: {
                label: translate('dashboard.custom_card.field_label.default'),
                value: {
                  name: WidgetNativeValues.ARR,
                  fieldClass: FieldClass.NativeField,
                },
              },
              prefix: false,
            }}
          />
        );
      case Step.Four:
        return (
          <CustomChart
            onFinish={createCustomChartWidget}
            defaultGroup={FilterGroupType.Project}
            groupedFields={groupedFields}
            defaultGroupedFields={defaultGroupedFields}
            transformer={transformer}
            form={chartFieldsForm}
            filterFields={customChartsFilter}
            setFilter={setCustomChartsFilter}
            initialValues={customChartInitialValues}
            isEdit={false}
          />
        );
      default:
        return unreachable(currentStep);
    }
  };

  return (
    <DashBoardDrawer
      className="dashboard-widget-drawer"
      large={currentStep === Step.Three || currentStep === Step.Four}
      gray={currentStep === Step.One}
      visible={visible}
      onClose={closeDrawer}
      title={
        <>
          <PrimaryTitle>
            {currentStep === Step.Three ? (
              <FormattedMessage id="dashboard.new_card" />
            ) : (
              <FormattedMessage id="dashboard.new_chart" />
            )}
          </PrimaryTitle>
          {currentStep === Step.Two ? (
            <SecondaryTitle>
              <FormattedMessage
                id="dashboard.new_chart.from_type"
                values={{
                  widgetTypeName: widgetTypeToName(selectedWidgetType),
                }}
              />
            </SecondaryTitle>
          ) : null}
        </>
      }
      afterVisibleChange={handleVisibleChange}
      footer={getFooter(currentStep)}
    >
      {content()}
    </DashBoardDrawer>
  );
};
